/**
 * The Shopify ↔ ERPNext order match (#3379): pure functions over rows read from the cell.
 *
 * Rules, in order:
 *  1. Reference: the store order carries the ERPNext Sales Order its sync recorded. Same total →
 *     matched; a different total → amount mismatch; a second store order on the same reference →
 *     duplicate; a reference ERPNext does not have → missing in ERP.
 * *  2. Customer + amount + date: an unreferenced store order pairs with the unclaimed ERPNext order
 *     that has the same currency and total, placed within the date tolerance, preferring the one
 *     booked to the same customer. These pairs always need review.
 *  3. ERPNext orders in the store's window that nothing paired are missing in Shopify.
 *
 * The customer is a signal, never a key: the store order's email against the email of the customer
 * ERPNext booked the order to. An email the reader may not see is "unknown", not a disagreement.
 */

export type Reason = "matched" | "amount_mismatch" | "missing_in_erp" | "missing_in_shopify" | "duplicate"
export type Rule = "reference" | "amount_date" | "none"
/** Whether both sides name the same customer; unknown when either email is missing or hidden. */
export type CustomerSignal = "same" | "different" | "unknown"

export interface ShopRow {
  /** The object key (gid-shopify-Order-<id>). */
  readonly key: string
  readonly name: string
  /** Shopify's money text, exactly as received. */
  readonly totalText: string
  readonly total: number | null
  readonly currency: string
  /** Shopify's timestamp text, exactly as received. */
  readonly createdAt: string
  /** The calendar day in the store's own offset (the date part of `createdAt`). */
  readonly date: string | null
  readonly status: string
  readonly email: string | null
  readonly emailHidden: boolean
  readonly reference: string | null
  /** The record's version: a changed order is a new question for review. */
  readonly version: number
}

export interface ErpRow {
  readonly key: string
  readonly number: string
  readonly date: string | null
  readonly total: number | null
  readonly currency: string
  readonly status: string
  /** The customer the order was booked to (party key), with its name and email when readable. */
  readonly customerKey: string | null
  readonly customer: string | null
  readonly customerHidden: boolean
  readonly customerEmail: string | null
  readonly customerEmailHidden: boolean
  /** The customer record's version (0 without one): its email is part of what was decided. */
  readonly customerVersion: number
  readonly version: number
}

export interface Pair {
  /** Stable across runs: the decision record's key. */
  readonly id: string
  readonly shop: ShopRow | null
  readonly erp: ErpRow | null
  readonly reason: Reason
  readonly rule: Rule
  /** 0–100: how sure the rule is that this verdict is right. */
  readonly confidence: number
  /** Total difference, store minus ERP, when both sides have one. */
  readonly delta: number | null
  /** Days between the two order dates, when both have one. */
  readonly dayGap: number | null
  /** Other ERP orders the amount + date rule could also have paired. */
  readonly alternatives: readonly string[]
  readonly customer: CustomerSignal
}

export interface MatchSettings {
  /** Totals within this many currency units agree. */
  readonly amountTolerance: number
  /** Unreferenced orders pair only within this many days. */
  readonly dayTolerance: number
}

export const DEFAULT_SETTINGS: MatchSettings = { amountTolerance: 0.005, dayTolerance: 1 }

/** Money text to a number; null for anything that is not plain decimal text. */
export function parseMoney(text: string | null | undefined): number | null {
  if (typeof text !== "string" || !/^-?\d+(\.\d+)?$/.test(text.trim())) return null
  const value = Number(text.trim())
  return Number.isFinite(value) ? value : null
}

/** The calendar day of an ISO timestamp in its own offset (what the store showed the customer). */
export function localDate(timestamp: string | null | undefined): string | null {
  const m = typeof timestamp === "string" ? /^(\d{4}-\d{2}-\d{2})T/.exec(timestamp) : null
  const date = m?.[1]
  // A real calendar day only: 2026-02-30 or 2026-99-99 is unreadable, never a crash later.
  if (!date) return null
  const parsed = Date.parse(`${date}T00:00:00Z`)
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === date ? date : null
}

export function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000)
}

/** Monday of the ISO week a day falls in. */
export function weekOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}

export function pairId(shop: ShopRow | null, erp: ErpRow | null): string {
  return `${shop?.key ?? "none"}~${erp?.key ?? "none"}`
}

/**
 * The decision record's key: the pair at the versions the reviewer saw. When either order or the
 * booked customer changes (a corrected total, a late reference, a new email), a version moves and
 * the pair is back in the queue.
 */
export function decisionKey(p: Pair): string {
  return `${p.id}~v${p.shop?.version ?? 0}.${p.erp?.version ?? 0}.${p.erp?.customerVersion ?? 0}`
}

/** An email for comparison: surrounding space and letter case never tell two people apart. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

export function customerSignal(shop: ShopRow, erp: ErpRow): CustomerSignal {
  if (shop.emailHidden || erp.customerEmailHidden || !shop.email || !erp.customerEmail) return "unknown"
  return normalizeEmail(shop.email) === normalizeEmail(erp.customerEmail) ? "same" : "different"
}

/** The ERP orders this match covers: those dated inside the store's window (a day of slack each side). */
export function storeWindow(shop: readonly ShopRow[], dayTolerance: number): { from: string; to: string } | null {
  const dates = shop.flatMap((s) => (s.date ? [s.date] : [])).sort()
  const first = dates[0]
  const last = dates.at(-1)
  if (!first || !last) return null
  const shift = (date: string, days: number) => {
    const d = new Date(`${date}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + days)
    return d.toISOString().slice(0, 10)
  }
  return { from: shift(first, -dayTolerance), to: shift(last, dayTolerance) }
}

const CUSTOMER_RANK: Record<CustomerSignal, number> = { same: 0, unknown: 1, different: 2 }

const agree = (a: number | null, b: number | null, tolerance: number) => a !== null && b !== null && Math.abs(a - b) <= tolerance

export function match(shopRows: readonly ShopRow[], erpRows: readonly ErpRow[], settings: MatchSettings = DEFAULT_SETTINGS): Pair[] {
  const window = storeWindow(shopRows, settings.dayTolerance)
  const inWindow = (e: ErpRow) => !window || (e.date !== null && e.date >= window.from && e.date <= window.to)
  const erpByNumber = new Map(erpRows.map((e) => [e.number, e]))
  const claimed = new Set<string>()
  const pairs: Pair[] = []
  const ordered = [...shopRows].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.key.localeCompare(b.key))
  const gap = (s: ShopRow, e: ErpRow) => (s.date && e.date ? dayDiff(s.date, e.date) : null)
  const delta = (s: ShopRow, e: ErpRow) => (s.total !== null && e.total !== null ? Math.round((s.total - e.total) * 100) / 100 : null)

  // 1. Reference.
  const unreferenced: ShopRow[] = []
  for (const s of ordered) {
    if (!s.reference) {
      unreferenced.push(s)
      continue
    }
    const e = erpByNumber.get(s.reference)
    if (!e) {
      pairs.push({ id: pairId(s, null), shop: s, erp: null, reason: "missing_in_erp", rule: "reference", confidence: 90, delta: null, dayGap: null, alternatives: [], customer: "unknown" })
      continue
    }
    const customer = customerSignal(s, e)
    const base = { id: pairId(s, e), shop: s, erp: e, rule: "reference" as const, delta: delta(s, e), dayGap: gap(s, e), alternatives: [], customer }
    if (claimed.has(e.key)) pairs.push({ ...base, reason: "duplicate", confidence: 95 })
    // A reference naming another customer's order is still the store's claim, but a person should look.
    else if (agree(s.total, e.total, settings.amountTolerance) && s.currency === e.currency) pairs.push({ ...base, reason: "matched", confidence: customer === "different" ? 80 : 100 })
    else pairs.push({ ...base, reason: "amount_mismatch", confidence: 90 })
    claimed.add(e.key)
  }

  // 2. Amount + date, over ERP orders no reference claimed.
  const open = erpRows.filter((e) => !claimed.has(e.key) && inWindow(e))
  for (const s of unreferenced) {
    const candidates = open
      .filter((e) => !claimed.has(e.key) && e.currency === s.currency && agree(s.total, e.total, settings.amountTolerance))
      .flatMap((e) => {
        const g = gap(s, e)
        return g !== null && Math.abs(g) <= settings.dayTolerance ? [{ e, gap: g, customer: customerSignal(s, e) }] : []
      })
      // The same customer first, then the nearest day.
      .sort((a, b) => CUSTOMER_RANK[a.customer] - CUSTOMER_RANK[b.customer] || Math.abs(a.gap) - Math.abs(b.gap) || a.e.key.localeCompare(b.e.key))
    const best = candidates[0]
    if (!best) {
      pairs.push({ id: pairId(s, null), shop: s, erp: null, reason: "missing_in_erp", rule: "amount_date", confidence: 80, delta: null, dayGap: null, alternatives: [], customer: "unknown" })
      continue
    }
    claimed.add(best.e.key)
    // One candidate booked to the same customer settles a tie on amount and date.
    const ambiguous = candidates.length > 1 && !(best.customer === "same" && candidates.filter((c) => c.customer === "same").length === 1)
    const confidence = best.customer === "different" ? 40 : ambiguous ? 50 : (best.gap === 0 ? 85 : 70) + (best.customer === "same" ? 10 : 0)
    pairs.push({
      id: pairId(s, best.e),
      shop: s,
      erp: best.e,
      reason: "matched",
      rule: "amount_date",
      confidence,
      delta: delta(s, best.e),
      dayGap: best.gap,
      alternatives: ambiguous ? candidates.slice(1).map((c) => c.e.number) : [],
      customer: best.customer,
    })
  }

  // 3. ERP orders in the window that nothing paired.
  for (const e of erpRows) {
    if (claimed.has(e.key) || !inWindow(e)) continue
    pairs.push({ id: pairId(null, e), shop: null, erp: e, reason: "missing_in_shopify", rule: "none", confidence: 75, delta: null, dayGap: null, alternatives: [], customer: "unknown" })
  }
  return pairs
}

/** A pair a person should look at: anything but a reference match with agreeing totals and customers. */
export function needsReview(p: Pair): boolean {
  return p.reason !== "matched" || p.rule !== "reference" || p.customer === "different"
}

export interface Summary {
  readonly total: number
  /** Pairs no one needs to look at: reference matches whose totals and customers agree. */
  readonly clean: number
  readonly byReason: Readonly<Record<Reason, number>>
  readonly fuzzy: number
  readonly review: number
  readonly outOfWindow: number
}

export function summarize(pairs: readonly Pair[], erpRows: readonly ErpRow[]): Summary {
  const byReason: Record<Reason, number> = { matched: 0, amount_mismatch: 0, missing_in_erp: 0, missing_in_shopify: 0, duplicate: 0 }
  for (const p of pairs) byReason[p.reason]++
  const paired = new Set(pairs.flatMap((p) => (p.erp ? [p.erp.key] : [])))
  return {
    total: pairs.length,
    clean: pairs.filter((p) => p.reason === "matched" && !needsReview(p)).length,
    byReason,
    // Matched pairs that still need a person: amount + date pairs, and references naming another customer.
    fuzzy: pairs.filter((p) => p.reason === "matched" && needsReview(p)).length,
    review: pairs.filter(needsReview).length,
    outOfWindow: erpRows.filter((e) => !paired.has(e.key)).length,
  }
}

/** Unmatched pairs per ISO week (by the store's date, else the ERP date), split by reason. */
export function weeklyUnmatched(pairs: readonly Pair[]): { week: string; amount_mismatch: number; missing_in_erp: number; missing_in_shopify: number; duplicate: number; review: number }[] {
  const weeks = new Map<string, { week: string; amount_mismatch: number; missing_in_erp: number; missing_in_shopify: number; duplicate: number; review: number }>()
  for (const p of pairs) {
    const date = p.shop?.date ?? p.erp?.date
    if (!date) continue
    const week = weekOf(date)
    const row = weeks.get(week) ?? { week, amount_mismatch: 0, missing_in_erp: 0, missing_in_shopify: 0, duplicate: 0, review: 0 }
    if (p.reason === "matched") {
      if (needsReview(p)) row.review++
    } else row[p.reason]++
    weeks.set(week, row)
  }
  return [...weeks.values()].sort((a, b) => a.week.localeCompare(b.week))
}
